import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Cookies from 'js-cookie';
import { useEffect, useState } from 'react';
import { api } from '../../../../shared/services/api';
import type { IResponseAjud } from '../../../../shared/services/types/response';
import type {
  IExecutionLogsResponse,
  IBlockSummaryResponse,
} from '../../types';
import {
  StyledTableCellBody,
  StyledTableCellHead,
} from '../styledTableCell';

interface AjudAuditModalProps {
  open: boolean;
  onClose: () => void;
  record: IResponseAjud | null;
  hasAuditLog: boolean;
  onConfirm: (recordId: string, retainedValue: number) => void;
}

const formatCurrency = (value: number) =>
  `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

const parseCurrency = (raw: string | number | null | undefined): number => {
  if (raw == null || raw === '') return NaN;
  const str = String(raw);
  const normalized = str.includes(',')
    ? str.replace(/\./g, '').replace(',', '.')
    : str;
  return Number(normalized);
};

const formatValue = (raw: string | number | null | undefined): string => {
  const val = parseCurrency(raw);
  return isNaN(val) ? '-' : formatCurrency(val);
};

const typeRecordLabel: Record<string, string> = {
  '03': 'Bloqueio',
  '05': 'Desbloqueio',
  '07': 'Transferência',
};

const AjudAuditModal = ({
  open,
  onClose,
  record,
  hasAuditLog,
  onConfirm,
}: AjudAuditModalProps) => {
  const theme = useTheme();
  const token = Cookies.get('@token');
  const isDone = record?.status === 'DONE';

  const [retainedValue, setRetainedValue] = useState('');
  const [error, setError] = useState('');

  // Dados das novas rotas
  const [executionLogs, setExecutionLogs] = useState<IExecutionLogsResponse | null>(null);
  const [blockSummary, setBlockSummary] = useState<IBlockSummaryResponse | null>(null);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const hasAuditData = (executionLogs != null && executionLogs.logs.length > 0) || hasAuditLog;

  const getRecordValue = () => {
    if (!record) return 0;
    const raw =
      record.type_record === '05' ? record.value_unlock
      : record.type_record === '07' ? record.value_transference
      : record.lock_value;
    if (raw == null || raw === '') return 0;
    const normalized = String(raw).includes(',')
      ? String(raw).replace(/\./g, '').replace(',', '.')
      : String(raw);
    const val = Number(normalized);
    return isNaN(val) ? 0 : val;
  };

  const recordValue = getRecordValue();

  useEffect(() => {
    if (open && record?.court_order) {
      fetchData(record.court_order);
    }
    if (!open) {
      setExecutionLogs(null);
      setBlockSummary(null);
    }
  }, [open, record?.court_order]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async (courtOrder: string) => {
    const headers = { Authorization: 'Bearer ' + token };
    setIsLoadingData(true);

    try {
      const [logsRes, summaryRes] = await Promise.all([
        api.get<IExecutionLogsResponse>(`/ajudArchive/executionLogs/courtOrder/${courtOrder}`, { headers }),
        api.get<IBlockSummaryResponse>(`/ajudArchive/blockSummary/courtOrder/${courtOrder}`, { headers }),
      ]);
      setExecutionLogs(logsRes.data);
      setBlockSummary(summaryRes.data);
    } catch {
      // Silencioso — as seções ficam vazias
    } finally {
      setIsLoadingData(false);
    }
  };

  const handleClose = () => {
    setRetainedValue('');
    setError('');
    onClose();
  };

  const handleValueChange = (value: string) => {
    const sanitized = value.replace(/[^0-9.,]/g, '');
    setRetainedValue(sanitized);

    const parsed = parseFloat(sanitized.replace(',', '.'));
    if (isNaN(parsed)) {
      setError('');
      return;
    }

    if (parsed > recordValue) {
      setError(`O valor não pode ser maior que ${formatCurrency(recordValue)}`);
    } else if (parsed < 0) {
      setError('O valor não pode ser negativo');
    } else {
      setError('');
    }
  };

  const handleConfirm = () => {
    if (!record) return;

    const parsed = parseFloat(retainedValue.replace(',', '.'));
    if (isNaN(parsed) || parsed < 0) {
      setError('Informe um valor válido');
      return;
    }
    if (parsed > recordValue) {
      setError(`O valor não pode ser maior que ${formatCurrency(recordValue)}`);
      return;
    }

    onConfirm(record.id, parsed);
    handleClose();
  };

  if (!record) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.primary.main,
          color: '#fff',
        }}
      >
        Conciliação e Aceite Manual
        <IconButton onClick={handleClose} size="small" sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 2, mt: 1 }}>
        {/* Protocolo em destaque */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            Protocolo
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {record.court_order}
          </Typography>
        </Box>

        {/* Info do registro */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 2,
            mb: 3,
            p: 2,
            borderRadius: 1,
            bgcolor: theme.palette.background.default,
          }}
        >
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Valor
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
              {formatCurrency(recordValue)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Status
            </Typography>
            <Typography variant="body2">{record.status}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Tipo
            </Typography>
            <Typography variant="body2">
              {typeRecordLabel[record.type_record] ?? record.type_record}
            </Typography>
          </Box>
        </Box>

        {/* Loading dos dados */}
        {isLoadingData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {/* Dropdowns de dados */}
        {!isLoadingData && (
          <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {/* Dropdown Resumo Auditoria */}
            {blockSummary && (
              <Accordion defaultExpanded={blockSummary.records.length > 0} disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Resumo Bloqueio ({blockSummary.total_records})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  {/* Totais */}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr 1fr',
                      gap: 2,
                      px: 2,
                      pb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Total Bloqueado
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(blockSummary.total_lock_value)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Valor Retido
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>
                        {formatCurrency(blockSummary.retained_value)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        Registros
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {blockSummary.total_records}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Tabela de registros */}
                  {blockSummary.records.length > 0 && (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <StyledTableCellHead>Status</StyledTableCellHead>
                          <StyledTableCellHead align="right">Valor Bloqueio</StyledTableCellHead>
                          <StyledTableCellHead align="right">Valor Disponível</StyledTableCellHead>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {blockSummary.records.map((rec, idx) => (
                          <TableRow key={rec.id || idx}>
                            <StyledTableCellBody>{rec.status || '-'}</StyledTableCellBody>
                            <StyledTableCellBody align="right">
                              {formatValue(rec.lock_value)}
                            </StyledTableCellBody>
                            <StyledTableCellBody align="right">
                              {formatValue(rec.account_value_available)}
                            </StyledTableCellBody>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </AccordionDetails>
              </Accordion>
            )}

            {/* Dropdown Logs de Execução */}
            {executionLogs && executionLogs.logs.length > 0 && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                    Resumo Auditoria - (Logs de Execução) ({executionLogs.total_records})
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <StyledTableCellHead>Agência</StyledTableCellHead>
                        <StyledTableCellHead>Conta</StyledTableCellHead>
                        <StyledTableCellHead>Dígito</StyledTableCellHead>
                        <StyledTableCellHead align="right">Valor Bloqueado</StyledTableCellHead>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {executionLogs.logs.map((log, idx) => (
                        <TableRow key={`${log.legacy_protocol_id}-${idx}`}>
                          <StyledTableCellBody>{log.agency || '-'}</StyledTableCellBody>
                          <StyledTableCellBody>{log.account || '-'}</StyledTableCellBody>
                          <StyledTableCellBody>{log.digit || '-'}</StyledTableCellBody>
                          <StyledTableCellBody align="right">
                            {log.value_blocked != null
                              ? formatCurrency(Number(log.value_blocked))
                              : '-'}
                          </StyledTableCellBody>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      px: 2,
                      py: 1,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Total bloqueado (logs):{' '}
                      <strong>{formatCurrency(executionLogs.total_value_blocked)}</strong>
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Box>
        )}

        {/* Diferença Auditada — só aparece se blockSummary tem registros */}
        {!isLoadingData && blockSummary && blockSummary.records.length > 0 && executionLogs && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 1,
              bgcolor: theme.palette.background.default,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
              Diferença Auditada:{' '}
              <Typography component="span" variant="subtitle2" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
                {formatCurrency(
                  blockSummary.records.reduce((sum, rec) => sum + parseCurrency(rec.account_value_available), 0)
                  - executionLogs.total_value_blocked
                )}
              </Typography>
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Valor Disponível (Resumo Bloqueio) - Valor Bloqueado (Resumo Auditoria)
            </Typography>
          </Box>
        )}

        {/* Campo de valor retido */}
        {isDone ? (
          <Box
            sx={{
              p: 2,
              borderRadius: 1,
              bgcolor: '#e8f5e9',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2" sx={{ color: '#2e7d32' }}>
              Este registro já foi processado (DONE). Nenhuma alteração permitida.
            </Typography>
          </Box>
        ) : (
          <Box>
            {!hasAuditData && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1,
                  p: 1,
                  borderRadius: 1,
                  bgcolor: '#fff8e1',
                }}
              >
                <WarningAmberIcon sx={{ color: '#f9a825', fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: '#e65100' }}>
                  Registro sem rastro de auditoria. Informe o valor efetivamente retido.
                </Typography>
              </Box>
            )}

            <TextField
              label="Valor Efetivamente Retido"
              size="small"
              fullWidth
              value={retainedValue}
              onChange={(e) => handleValueChange(e.target.value)}
              error={!!error}
              helperText={error || `Máximo: ${formatCurrency(recordValue)}`}
              placeholder="0,00"
              sx={{ mt: 1 }}
              InputProps={{
                startAdornment: (
                  <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>
                ),
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit">
          Cancelar
        </Button>
        {!isDone && (
          <Button
            onClick={handleConfirm}
            variant="contained"
            disabled={!retainedValue || !!error}
            sx={{
              bgcolor: theme.palette.primary.main,
              '&:hover': { bgcolor: theme.palette.primary.dark },
            }}
          >
            Confirmar Aceite
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AjudAuditModal;
